import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { settingsAPI } from '../api';
import { setFilters, setMonths, setSavedOrderExists, setSearch, setSort } from './orderSlice';
import { accessor, formatAddress, getLocalUTCDate, getLocalUTCDateTimestamp, getLocalUTCTime, getMainUTCMonth, getStartOfMonthTimestamp, isValidOrderFilters } from '../helpers';
import { orderStatuses } from '../names';
import { setOfficeMonths } from './officeSlice';
import { CLIENT_NOTS, getClientNotify } from './notifySlice';
import { setAdvertMonths } from './advertSlice';


export const getZoomSettings = (min, max) => {
    try {
        const zoom = +localStorage.getItem('zoomState');
        if (!zoom || isNaN(zoom) || typeof zoom !== 'number' || zoom < min || zoom > max) {
            return 1;
        }
        return zoom;
    } catch (error) {
        return 1;
    }
}
export const regularizeOrders = (order) => {
    if (!order.completionDate) {//Самые первые - без даты завершения (в процессе и неназначенные)
        if (!order.details.assigned.by) {
            return {...order, seqNumber: 4}
        }
        return {...order, seqNumber: 3};    //Назначенные
    }
    if (order.orderStatus.name === orderStatuses.MODERN || order.orderStatus.name === orderStatuses.CANCEL) {
        if (order.orderStatus.name === orderStatuses.MODERN) {
            return {...order, seqNumber: 0};
        } else {
            return {...order, seqNumber: 1};
        }
    }
    return {...order, seqNumber: 2};
}

export function createSortOrdersFunc({key, isASC}) {
    switch(key) {
        case 'id':
        case 'totalAmount':
        case 'partsCost':
        case 'clearAmount':
        case 'handOver': {
            return (a, b) => {
                return isASC ? a[key] - b[key] : b[key] - a[key]
            }
        }
        case 'advertType':
        case 'orderType':
        case 'city':
        case 'profileType':
        case 'orderStatus':
        case 'worker': {
            return (a, b) => {
                const key2 = key === 'worker' ? 'fullname' : 'name';
                if (!a[key]) {
                    return isASC ? 1 : -1
                }
                if (!b[key]) {
                    return isASC ? -1 : 1
                }
                return isASC ? a[key][key2] > b[key][key2] ? 1 : -1 : a[key][key2] < b[key][key2] ? 1 : -1;
            }
        }
        case 'address': {
            return (a, b) => {
                const address1 = `${a.address.village} ${a.address.street} ${a.address.apartmentNumber} ${a.address.additional}`
                const address2 = `${b.address.village} ${b.address.street} ${b.address.apartmentNumber} ${b.address.additional}`
                return isASC ? address1 > address2 ? 1 : -1 : address1 < address2 ? 1 : -1;
            }
        }
        case 'date': {
            return (a, b) => {
                const key2 = a.date.min !== b.date.min ? 'min' : a.date.max !== b.date.max ? 'max' : null
                if (!key2) return 0;
                return isASC ? a[key][key2] - b[key][key2] : b[key][key2] - a[key][key2];
            }
        }
        case 'time': {
            return (a, b) => {
                const UTC1 = a.city.UTC;
                const UTC2 = b.city.UTC;
                const time1 = String(getLocalUTCTime(UTC1, a.date, true));
                const time2 = String(getLocalUTCTime(UTC2, b.date, true));
                return isASC ? time1 > time2 ? 1 : -1 : time1 < time2 ? 1 : -1;
            }
        }
        case 'workerComment': {
            return (a, b) => {
                if (!a[key]) {
                    return isASC ? 1 : -1
                }
                if (!b[key]) {
                    return isASC ? -1 : 1
                }
                return isASC ? a[key] > b[key] ? 1 : -1 : a[key] < b[key] ? 1 : -1;
            }
        }
        case 'completionDate': {
            return (a, b) => {
                if (a.seqNumber === b.seqNumber) {
                    return isASC ? a[key] - b[key] : b[key] - a[key]
                }
                return isASC ? a.seqNumber - b.seqNumber : b.seqNumber - a.seqNumber
            }
        }
        default: {
            return (a, b) => {
                return isASC ? a[key] > b[key] ? 1 : -1 : a[key] < b[key] ? 1 : -1;
            }
        }
    }
}

export function createSearchOrdersFunc({value, type}) {
    const val = value.toLowerCase().trim();
    const format = (val) => String(val).toLowerCase().trim();

    const filterByEnums = (order) => {
        const keys = ['advertType', 'orderType', 'city', 'profileType', 'orderStatus', 'worker'];
        if (type === 'all') {
            for (let i = 0; i < keys.length; i++) {
                if (keys[i] === 'worker') {
                    if (val === '') {
                        return !order.worker;
                    }
                    if (!order.worker) {    //Нет мастера, но есть текст
                        return false;
                    }
                }
                const name = keys[i] === 'worker' ? order.worker.fullname : order[keys[i]].name;
                if (format(name).includes(val, 0)) {
                    return true;
                }
            }
            return false;
        }
        if (type === 'worker') {
            if (val === '') { //Нет текста и мастер есть/нет => false/true
                return !order.worker;
            }
            if (!order.worker) {    //Нет мастера, но есть текст
                return false;
            }
        }
        const name = type === 'worker' ? order[type].fullname : order[type].name;
        return format(name).includes(val, 0);
    }
    const filterByAddress = (order) => {
        const address = formatAddress(order.address);
        return format(address).includes(val, 0);
    }
    const filterByDate = (order) => {
        const date = getLocalUTCDate(order.city.UTC, order.date.min);
        return format(date).includes(val, 0);
    }
    const filterByTime = (order) => {
        const time = String(getLocalUTCTime(order.city.UTC, order.date, true))
        return format(time).includes(val, 0);
    }
    const filterByPhone = (order) => {
        const phoneVar1 = order.clientPhone;
        const phoneVar2 = order.clientPhone.split(' ').join('');
        const phoneVar3 = order.clientPhone.split('-').join('')
        const phoneVar4 = order.clientPhone.replace(/[\s()-]/g, '');
        const phoneVar5 = order.clientPhone.split(' ').join('').split('-').join('')
        return format(phoneVar1).includes(val, 0) || format(phoneVar2).includes(val, 0) ||
        format(phoneVar3).includes(val, 0) || format(phoneVar4).includes(val, 0) ||
        format(phoneVar5).includes(val, 0);
    }
    const filterByIsProfile = (order) => {
        const name = order.isProfile ? 'профиль' : 'непрофиль'
        return format(name).includes(val, 0);
    }
    const filterByCompletionDate = (order) => {
        const completionDate = order.completionDate 
        ? getLocalUTCDate(order.city.UTC, order.completionDate) 
        : '';
        return format(completionDate).includes(val, 0);
    }
    const filterByDefault = (order) => {
        const keys = ['id', 'sourceType', 'clientName', 'orderComment', 'totalAmount', 'partsCost', 'clearAmount', 'handOver', 'workerComment'];
        if (type === 'all') {
            for (let i = 0; i < keys.length; i++) {
                if (format(order[keys[i]]).includes(val, 0)) {
                    return true;
                }
            }
            return false;
        }
        return format(order[type]).includes(val, 0);
    }

    // const enumSearchFn = 
    switch(type) {
        case 'all':
            return (order) => {
                // Проверяем результаты всех функций фильтрации
                return (
                    filterByEnums(order) ||
                    filterByAddress(order) ||
                    filterByDate(order) ||
                    filterByTime(order) ||
                    filterByPhone(order) ||
                    filterByIsProfile(order) ||
                    filterByCompletionDate(order) ||
                    filterByDefault(order)
                );
            };
        case 'advertType':
        case 'orderType':
        case 'city':
        case 'profileType':
        case 'orderStatus':
        case 'worker': return filterByEnums;
        case 'address': return filterByAddress;
        case 'date': return filterByDate;
        case 'time': return filterByTime;
        case 'clientPhone': return filterByPhone;
        case 'isProfile': return filterByIsProfile;
        case 'completionDate': return filterByCompletionDate;
        default: return filterByDefault;
    }
}

export function createFilterOrdersFunc(filter) {

    const advertTypeFilter = filter.find(f => f.key === 'advertType')
    const filterByAdvertType = (order) => advertTypeFilter.selected.some((str) => str === order.advertType._id);

    const orderTypeFilter = filter.find(f => f.key === 'orderType')
    const filterByOrderType = (order) => orderTypeFilter.selected.some((str) => str === order.orderType._id);

    const cityFilter = filter.find(f => f.key === 'city')
    const filterByCity = (order) => cityFilter ? cityFilter.selected.some((str) => str === order.city._id) : true;

    const profileTypeFilter = filter.find(f => f.key === 'profileType')
    const filterByProfileType = (order) => profileTypeFilter ? profileTypeFilter.selected.some((str) => str === order.profileType._id) : true;

    const isProfileFilter = filter.find(f => f.key === 'isProfile')
    const filterByIsProfile = (order) => isProfileFilter.selected.some((str) => str === String(order.isProfile));

    const orderStatusFilter = filter.find(f => f.key === 'orderStatus')
    const filterByOrderStatus = (order) => orderStatusFilter.selected.some((str) => str === order.orderStatus._id);

    const workerFilter = filter.find(f => f.key === 'worker')
    const filterByWorker = (order) => workerFilter
    ? workerFilter.list.length === workerFilter.selected.length 
        ? true
        : order.worker
            ? workerFilter.selected.some((str) => str === order.worker._id)
            : workerFilter.selected.some((str) => str === 'null') ? true : false
    : true;

    const creatorFilter = filter.find(f => f.key === 'creator');
    const filterByCreator = (order) => creatorFilter
    ? creatorFilter.list.length === creatorFilter.selected.length 
        ? true
        : creatorFilter.selected.some((str) => str === order.details.created.for)
    : true;

    const orderDateFilter = filter.find(f => f.key === 'date');
    const clearAmountFilter = filter.find(f => f.key === 'clearAmount');
    const orderComplDateFilter = filter.find(f => f.key === 'completionDate');
    const filterByOrderDate = (dateStamp, filter) => (order) => {
        const {min, max} = filter.interval;
        if (!dateStamp) {   //Нет даты завершения
            if (!min && !max) { //Её и не вводили
                return true;
            }
        }
        if (!min && !max) return true;
        const UTC = order.city.UTC;
        const localMinStamp = min ? getLocalUTCDateTimestamp(UTC, `${min}T00:00`) : null;
        const localMaxStamp = max ? getLocalUTCDateTimestamp(UTC, `${max}T23:59`) : null;
        
        if (min && max) {
            return dateStamp >= localMinStamp && dateStamp <= localMaxStamp;
        }
        if (min) {
            return dateStamp >= localMinStamp;
        }
        if (max) {
            return dateStamp <= localMaxStamp;
        }
    }

    const filterByClearAmount = (order) => {
        if (!clearAmountFilter) return true;
        const {min, max} = clearAmountFilter.interval;
        const {clearAmount} = order;
        if (!min && !max) return true;
        if (min && max) {
            return clearAmount >= +min && clearAmount < +max;
        }
        if (min) {
            return clearAmount >= +min;
        }
        return clearAmount < +max
    }


    return (order) => {
        return (
            filterByAdvertType(order) &&
            filterByOrderType(order) &&
            filterByCity(order) &&
            filterByProfileType(order) &&
            filterByIsProfile(order) &&
            filterByOrderStatus(order) &&
            filterByWorker(order) &&
            filterByCreator(order) &&
            filterByClearAmount(order) &&
            filterByOrderDate(order.date.min, orderDateFilter)(order) &&
            filterByOrderDate(order.completionDate, orderComplDateFilter)(order)
        );
    }
}

function initMonths(firstMonthStr, mainUTC) {
    const curMonthStr = getMainUTCMonth(mainUTC);
    const firstMonth = new Date(`${firstMonthStr}-01`);
    const curMonth = new Date(`${curMonthStr}-01`);

    const months = [];
    let currentMonth = new Date(firstMonth);
    while (currentMonth <= curMonth) {
        const month = currentMonth.getMonth() + 1;
        const year = currentMonth.getFullYear();
        const monthStr = `${year}-${String(month).padStart(2, '0')}`;
        const loaded = false;
        const active = false;
        const timestamp = getStartOfMonthTimestamp(currentMonth, mainUTC);
        months.push({ month: monthStr, loaded, active, timestamp });
        currentMonth.setMonth(currentMonth.getMonth() + 1);
    }
    months[months.length-1].active = true;
    return months;
}

function initSort(titles) {
    try {
        const {key, isASC} = JSON.parse(localStorage.getItem('orderSort'));
        if (!titles.some(title => title.key === key ) || typeof isASC !== 'boolean') {
            throw new Error();
        }
        return {key, isASC};
    } catch (error) {
        return {
            key: 'completionDate',
            isASC: false
        }
    }
}
function initSearch(titles) {
    try {
        const {value, type} = JSON.parse(localStorage.getItem('orderSearch'));
        if (!titles.some(title => title.key === type || type === 'all' ) || typeof value !== 'string') {
            throw new Error();
        }
        return {value, type};
    } catch (error) {
        return {
            value: '',
            type: 'all'
        }
    }
}

function initFilters(settings, userList) {
    const {advertTypes, orderTypes, orderUsers, orderCities, orderProfiles, orderStatuses} = settings;
    const savedFilters = JSON.parse(localStorage.getItem('orderFilter'));
    const isFiltersValid = isValidOrderFilters(savedFilters, settings, userList);

    if (savedFilters && isFiltersValid) {
        return savedFilters;
    }

    const filteredUserIdList = orderUsers.filter(u => userList.some(u2 => u2._id === u && !u2.deleted && u2.profiles.length));
    const filteredCreatorList = orderUsers.filter(u => userList.some(u2 => u2._id === u && !u2.deleted && accessor.orders(u2)));
    return [{
            key: 'advertType',
            list: advertTypes,
            selected: advertTypes.map(el => el._id),
        },{
            key: 'orderType',
            list: orderTypes,
            selected: orderTypes.map(el => el._id),
        }, ...(orderCities.length > 1 ? [{
            key: 'city',
            list: orderCities,
            selected: orderCities.map(el => el._id),
        }] : []),
         ...(orderProfiles.length > 1 ? [{
            key: 'profileType',
            list: orderProfiles,
            selected: orderProfiles.map(el => el._id),
        }] : []), {
            key: 'isProfile',
            list: [{_id: true, name: 'Профиль'}, {_id: false, name: 'Непрофиль'}],
            selected: ["true", "false"]
        }, {
            key: 'orderStatus',
            list: orderStatuses,
            selected: orderStatuses.map(el => el._id),
        }, ...(orderUsers.length > 1 ? [{
            key: 'worker',
            list: ['null', ...filteredUserIdList],
            selected: ['null', ...filteredUserIdList],
        }] : []),
            ...(orderUsers.length > 1 ? [{
            key: 'creator',
            list: filteredCreatorList,
            selected: filteredCreatorList,
        }] : []),
        {
            type: 'date',
            key: 'date',
            interval: {
                min: '',
                max: '',
            }
        },
        {
            type: 'date',
            key: 'completionDate',
            interval: {
                min: '',
                max: '',
            }
        },
        {
            type: 'number',
            key: 'clearAmount',
            interval: {
                min: '',
                max: '',
            }
        }
    ]
}

const initialState = {
    mainUTC: null,
    firstMonth: null,
    advertFirstMonth: null,
    botTelegramId: null,
    advertTypes: null,
    orderTypes: null,
    orderUsers: null,
    orderCities: null,
    orderProfiles: null,
    orderStatuses: null,
    orderTitles: null,

    availableUsers: null,
    userProfiles: null,
    userCities: null,

    officeCities: null,
    officeProfiles: null,
    expenseCategories: null,

    permissionSchema: null,

    calculation: null,

    advertCities: null,
    flyerTypes: null,
    advertSources: null,

    orderColors: null,
    themeColors: null,
};

export const fetchSettings = createAsyncThunk('settings/fetchSettings', async (userList, thunkAPI) => {
    const data = await settingsAPI.getSettings();
    if (data.isOkay) {
        thunkAPI.dispatch(setOptionsData(data.settings));

        const {firstMonth, advertFirstMonth, mainUTC} = data.settings;
        const months = initMonths(firstMonth, mainUTC);
        thunkAPI.dispatch(setMonths(months));
        
        thunkAPI.dispatch(setOfficeMonths(months));

        const advertMonths = initMonths(advertFirstMonth, mainUTC);
        thunkAPI.dispatch(setAdvertMonths(advertMonths));

        const orderSort = initSort(data.settings.orderTitles);
        thunkAPI.dispatch(setSort(orderSort));

        const orderSearch = initSearch(data.settings.orderTitles);
        thunkAPI.dispatch(setSearch(orderSearch));

        thunkAPI.dispatch(setSavedOrderExists(data.settings));
        const orderFilter = initFilters(data.settings, userList);
        thunkAPI.dispatch(setFilters(orderFilter));

    } else {
        alert(data.message);
    }
    return data.isOkay;
});

export const updateSettings = createAsyncThunk('settings/update', async ({updatedSets}, thunkAPI) => {
    const data = await settingsAPI.update(updatedSets);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.UPDATE_SETTINGS}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateCalculation(data.settings));
    } else {
        console.log(data)
    }
    return data.isOkay;
});
export const updateOrderColors = createAsyncThunk('settings/update-order-colors', async (newList, thunkAPI) => {
    const data = await settingsAPI.updateOrderColors(newList);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.UPDATE_ORDER_COLORS}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateOrderTypeColors(data.orderColors));
    } else {
        console.log(data)
    }
    return data.isOkay;
});
export const updateThemeColors = createAsyncThunk('settings/update-theme-colors', async (newProps, thunkAPI) => {
    const data = await settingsAPI.updateThemeColors(newProps);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.UPDATE_THEME_COLORS}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateTheme(data.themeColors));
    } else {
        console.log(data)
    }
    return data.isOkay;
});

const settingSlice = createSlice({
    name: 'enums',
    initialState,
    reducers: {
        setOptionsData(state, {payload}) {
            state.mainUTC = payload.mainUTC;
            state.firstMonth = payload.firstMonth;
            state.advertFirstMonth = payload.advertFirstMonth;
            state.botTelegramId = payload.botTelegramId;
            state.advertTypes = payload.advertTypes;
            state.orderTypes = payload.orderTypes;
            state.orderUsers = payload.orderUsers;
            state.orderCities = payload.orderCities;
            state.orderProfiles = payload.orderProfiles;
            state.orderStatuses = payload.orderStatuses;
            state.orderTitles = payload.orderTitles;

            state.availableUsers = payload.availableUsers;
            state.userProfiles = payload.userProfiles;
            state.userCities = payload.userCities;

            state.officeCities = payload.officeCities;
            state.officeProfiles = payload.officeProfiles;
            state.expenseCategories = payload.expenseCategories;

            state.permissionSchema = payload.permissionSchema;

            state.calculation = payload.calculation;

            state.advertCities = payload.advertCities;
            state.flyerTypes = payload.flyerTypes;
            state.advertSources = payload.advertSources;

            state.orderColors = payload.orderColors;
            state.themeColors = payload.themeColors;
        },
        clearSettingsState(state) {
            state.mainUTC = null;
            state.firstMonth = null;
            state.advertFirstMonth = null;
            state.botTelegramId = null;
            state.advertTypes = null;
            state.orderTypes = null;
            state.orderUsers = null;
            state.orderCities = null;
            state.orderProfiles = null;
            state.orderStatuses = null;
            state.orderTitles = null;
            state.availableUsers = null;
            state.userProfiles = null;
            state.userCities = null;

            state.officeCities = null;
            state.officeProfiles = null;
            state.expenseCategories = null;

            state.permissionSchema = null;

            state.calculation = null;

            state.advertCities = null;
            state.flyerTypes = null;
            state.advertSources = null;

            state.orderColors = null;
            state.themeColors = null;
        },

        addCategory(state, {payload}) {
            state.expenseCategories = [...state.expenseCategories, payload]
        },
        editCategory(state, {payload}) {
            state.expenseCategories = state.expenseCategories.map(c => c._id === payload._id ? payload : c);
        },
        deleteCategory(state, {payload}) {
            state.expenseCategories = state.expenseCategories.filter(c => c._id !== payload);
        },
        updateAvailableUsers(state, {payload}) {
            state.availableUsers = payload;
        },
        updateCalculation(state, {payload}) {
            state.calculation = payload;
        },
        updateOrderTypeColors(state, {payload}) {
            state.orderColors = payload;
        },
        updateTheme(state, {payload}) {
            state.themeColors = payload;
        }

    },
    extraReducers: (builder) => {},
});

export const { updateTheme, updateOrderTypeColors, setOptionsData, updateCalculation, addCategory, editCategory, deleteCategory, clearSettingsState, updateAvailableUsers } = settingSlice.actions;

export default settingSlice.reducer;
